<template>
  <app-page
    :title="`Terminate ${employee ? employee.full_name : 'Employee'}`"
    :error="error"
    :previous="[
      { label: 'Employees', to: { name: 'employees' } },
      {
        label: employee ? employee.full_name : 'Employee',
        to: { name: 'employee', params: { employeeId: $route.params.employeeId } },
      },
    ]"
    icon="user-slash"
  >
    <app-loading :loading="loading" />
    <template v-if="!loading && !saved">
      <!-- Guards the page itself, not just the button, so pasting the URL for a blocked
           employee can't reach the form. -->
      <sme-alert v-if="unavailableReason" level="warning">{{ unavailableReason }}</sme-alert>
      <template v-else>
        <p class="text-muted">
          Termination should be used to tell us when an employee's
          <strong>employment is ending</strong> and they will no longer be working at the business.
        </p>
        <p class="text-muted">
          Terminating {{ employee.full_name }} restricts their ability to access flexible pay, whilst still allowing
          them to login and manage active products they hold with {{ productName }} like loans, pensions, and savings.
          If the employee rejoins the business then they can be reactivated via the portal.
        </p>
        <sme-alert level="info" class="mb-3">
          If you are expecting to receive termination dates via your integrated HR system, keep using your integration
          and do not use this feature.
        </sme-alert>

        <ValidationObserver v-slot="v">
          <b-form @submit.prevent="v.handleSubmit(onSubmit)">
            <app-input
              v-model="terminationDate"
              type="date"
              name="terminationDate"
              label="When is their last day of employment? (required)"
              rules="required"
              :disabled="saving"
              :validate-immediate="false"
            />
            <page-sub-footer>
              <b-button data-testid="terminate-button" type="submit" variant="danger" :disabled="v.invalid || saving">
                <b-spinner v-if="saving" class="mr-2" small />
                {{ saving ? 'Terminating...' : 'Terminate employee' }}
              </b-button>
            </page-sub-footer>
          </b-form>
        </ValidationObserver>
      </template>
    </template>
    <sme-alert v-if="saved" level="good">
      <p>
        <strong>{{ employee.full_name }} has been terminated.</strong>
      </p>
      <!-- Both outcomes are a success; only the banking sentence differs. -->
      <p v-if="unenrolmentOutcome === PORTAL_UNENROLMENT_OUTCOME.INITIATED">
        Their access to flexible pay has been removed and their personal bank details are being returned to your payroll
        system.
      </p>
      <p v-else>Their access to flexible pay has been removed.</p>
      <p>
        If you need to reverse this, use the Rehire button on their profile - a payroll update alone will not bring them
        back.
      </p>
    </sme-alert>
  </app-page>
</template>

<script>
import { ValidationObserver } from 'vee-validate';
import ApiClient from '@/ApiClient';
import AppInput from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import AppPage from '@/components/AppPage.vue';
import PageSubFooter from '@/components/PageSubFooter.vue';
import SmeAlert from '@/components/atoms/SmeAlert.vue';
import { getTerminateUnavailableReason, PORTAL_UNENROLMENT_OUTCOME } from '@/utils/Employee';
import { DEFAULT_PARTNERSHIP_NAME, getPartnershipData } from '@/utils/partnership';

const { name: partnershipName } = getPartnershipData();

export default {
  name: 'EmployeeTerminate',
  components: { ValidationObserver, AppInput, AppLoading, AppPage, PageSubFooter, SmeAlert },
  data() {
    return {
      PORTAL_UNENROLMENT_OUTCOME,
      productName: partnershipName || DEFAULT_PARTNERSHIP_NAME,
      loading: true,
      saving: false,
      saved: false,
      error: undefined,
      employee: undefined,
      terminationDate: undefined,
      unenrolmentOutcome: undefined,
    };
  },
  computed: {
    unavailableReason() {
      return getTerminateUnavailableReason(this.employee);
    },
  },
  methods: {
    async onSubmit() {
      this.saving = true;

      try {
        const result = await ApiClient.employeeUnenrol(this.employee.employee_id, this.terminationDate);
        this.unenrolmentOutcome = result?.unenrolment;
        this.saved = true;
      } catch (error) {
        this.error = error.message;
      }

      this.saving = false;
    },
    loadData() {
      this.loading = true;
      ApiClient.getEmployee(this.$route.params.employeeId).then(
        employee => {
          this.employee = employee;
          this.loading = false;
        },
        error => {
          this.error = error.message;
          this.loading = false;
        },
      );
    },
  },
  mounted() {
    this.loadData();
  },
};
</script>
